import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { auth } from '../../../firebase';
import PropertyForm from './PropertyForm';
import PropertyCard from './PropertyCard';
import PropertyLogs from './PropertyLogs';
import BookingManagement from './BookingManagement';
// import SuperUserEditor from './SuperUserEditor'; // Removed - admin functions should be in dedicated admin area
import PropertySearch from './PropertySearch';
import UpcomingShowings from './UpcomingShowings';
import CompletedShowings from './CompletedShowings';
import Overview from './Overview';
import Calendar from './Calendar';
import Stats from './Stats';
import Support from './Support';
import ShowingProfile from '../../../pages/account/ShowingProfile';
import DemoPropertyBanner from './DemoPropertyBanner';
import Owners from './Owners';
import Residents from './Residents';
import Agents from './Agents';
import Buyers from './Buyers';
import Renters from './Renters';
import Prospects from './Prospects';
import { showingbookerAPI } from '../../../services/showingbookerAPI';
import axios from 'axios';

const Dashboard = styled.div`
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
`;


const TabContent = styled.div`
  padding: 30px;
`;


const PropertiesGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const ConnectionStatus = styled.div`
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-weight: bold;
  background: ${props => props.connected ? '#d4edda' : '#fff3cd'};
  color: ${props => props.connected ? '#155724' : '#856404'};
  border: 1px solid ${props => props.connected ? '#c3e6cb' : '#ffeaa7'};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const LoginPrompt = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;

const AddButton = styled.button`
  padding: 12px 24px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  fontSize: 16px;
  fontWeight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #0056b3;
  }
`;

// Generate secure random slug for booking URLs
const generateSecurePropertySlug = () => {
  // Generate a long, secure random string (32 characters)
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  // Add timestamp to ensure uniqueness
  const timestamp = Date.now().toString(36);
  return `${result}${timestamp}`;
};

const AgentDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // const [authCheckCount, setAuthCheckCount] = useState(0); // Unused variable
  const [selectedPropertyForLogs, setSelectedPropertyForLogs] = useState(null);
  const [selectedPropertyForBookings, setSelectedPropertyForBookings] = useState(null);
  const [propertyFilter, setPropertyFilter] = useState('listed');
  const [apiConnected, setApiConnected] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [userRole, setUserRole] = useState('agent');
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const location = useLocation();
  
  // Determine active content based on URL
  const getActiveContent = () => {
    const path = location.pathname;
    if (path === '/overview') return 'overview';
    if (path === '/listings') return 'property-listings';
    if (path === '/properties') return 'property-listings';
    if (path === '/owners') return 'owners';
    if (path === '/residents') return 'residents';
    if (path === '/agents') return 'agents';
    if (path === '/buyers') return 'buyers';
    if (path === '/renters') return 'renters';
    if (path === '/prospects') return 'prospects';
    if (path === '/calendar') return 'calendar';
    if (path === '/upcoming') return 'upcoming-showings';
    if (path === '/completed') return 'completed-showings';
    if (path === '/profile') return 'profile';
    if (path === '/stats') return 'stats';
    if (path === '/support') return 'support';
    // Handle legacy routes
    if (path === '/showingbooker/overview') return 'overview';
    if (path === '/showingbooker/properties') return 'property-listings';
    if (path === '/showingbooker/calendar') return 'calendar';
    if (path === '/showingbooker/upcoming') return 'upcoming-showings';
    if (path === '/showingbooker/completed') return 'completed-showings';
    if (path === '/showingbooker/stats') return 'stats';
    if (path === '/showingbooker/rd') return 'support';
    if (path === '/showingbooker/profile') return 'profile';
    if (path === '/showingbooker/dashboard') return 'property-listings';
    if (path === '/showingbooker/listings') return 'property-listings';
    if (path === '/showingbooker/properties') return 'property-listings';
    return 'overview'; // default to overview
  };
  
  const activeContent = getActiveContent();

  // Fetch user profile
  const fetchUserProfile = async (user) => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user-profile/${user.uid}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Check authentication status
  const checkAuthentication = () => {
    const user = auth.currentUser;
    
    console.log('🔐 Auth check:', { user: !!user, uid: user?.uid });
    
    if (user) {
      setIsAuthenticated(true);
      fetchUserProfile(user);
      return true;
    } else {
      setIsAuthenticated(false);
      setProperties([]); // Clear properties when not authenticated
      setUserProfile(null);
      return false;
    }
  };


  // Test backend connection with status reporting
  const testBackendConnection = async () => {
    try {
      console.log('🔌 Testing backend connection...');
      await showingbookerAPI.testConnection();
      console.log('✅ Backend connected successfully');
      setApiConnected(true);
      setConnectionMessage('✅ Connected to database - all changes are saved automatically');
      return true;
    } catch (error) {
      console.error('❌ Backend connection failed:', error);
      setApiConnected(false);
      setConnectionMessage('⚠️ Working offline - using local storage. Changes will need to be migrated later.');
      return false;
    }
  };

  // Load properties from backend API
  const loadPropertiesFromBackend = async () => {
    if (!checkAuthentication()) {
      console.log('❌ Not authenticated, not loading properties');
      return;
    }
    
    try {
      console.log('📡 Loading properties from backend...');
      const backendProperties = await showingbookerAPI.getProperties();
      console.log('📂 Loaded properties from backend:', backendProperties);
      
      // Convert backend format to frontend format if needed
      const formattedProperties = backendProperties.map(property => ({
        ...property,
        // Ensure consistent property format
        address: property.address_line1,
        unitNumber: property.unit_number,
        zipCode: property.zip_code,
        accessInstructions: property.showing_instructions,
        isOccupied: property.is_occupied,
        tenantName: property.tenant_name,
        tenantEmail: property.tenant_email,
        tenantPhone: property.tenant_phone,
        showings: property.showings_booked || 0,
        showingsBooked: property.showings_booked || 0,
        slug: property.slug || generateSecurePropertySlug()
      }));
      
      setProperties(formattedProperties);
      setFilteredProperties(formattedProperties); // Initialize filtered properties
      console.log('✅ Properties and filtered properties updated:', formattedProperties.length);
      
      // Also load dashboard stats
      const stats = await showingbookerAPI.getDashboardStats();
      setDashboardStats(stats);
      console.log('📊 Dashboard stats:', stats);
      
      // Super Admin access ONLY for CTO/Developer of ShowingBooker.com
      const isSuperAdmin = auth.currentUser?.email === 'david@codexdeus.com';
      if (isSuperAdmin) {
        setUserRole('admin');
        console.log('👑 Super Admin detected: ShowingBooker CTO/Developer');
      } else {
        setUserRole('agent');
        console.log('👤 Regular user (agent)');
      }
      
    } catch (error) {
      console.error('❌ Error loading properties from backend:', error);
      setProperties([]);
      setFilteredProperties([]);
    }
  };



  // Initial auth check and property loading
  useEffect(() => {
    console.log('🚀 AgentDashboard mounting...');
    const initializeDashboard = async () => {
      const isAuth = checkAuthentication();
      
      if (isAuth) {
        // Test backend connection first
        const backendConnected = await testBackendConnection();
        
        if (backendConnected) {
          // Load from backend
          await loadPropertiesFromBackend();
        }
      }
      
      setLoading(false);
    };
    
    initializeDashboard();
  }, []);

  // Monitor authentication state changes (check every 2 seconds)
  useEffect(() => {
    const authInterval = setInterval(() => {
      const wasAuthenticated = isAuthenticated;
      const isAuth = checkAuthentication();
      
      // If auth status changed
      if (wasAuthenticated !== isAuth) {
        console.log('🔄 Auth status changed:', wasAuthenticated, '->', isAuth);
        if (isAuth) {
          loadPropertiesFromBackend();
        } else {
          setProperties([]); // Clear properties on logout
        }
        // Auth status changed, properties will update automatically
      }
    }, 2000);

    return () => clearInterval(authInterval);
  }, [isAuthenticated]);


  // Add property (backend only)
  const addProperty = async (propertyData) => {
    if (!checkAuthentication()) {
      alert('❌ Please log in to add properties');
      return;
    }
    
    console.log('🚀 AgentDashboard received property data:', propertyData);
    console.log('🔍 Agent fields:', {
      use_public_profile: propertyData.use_public_profile,
      agent_first_name: propertyData.agent_first_name,
      agent_last_name: propertyData.agent_last_name,
      agent_email: propertyData.agent_email,
      agent_phone: propertyData.agent_phone
    });
    console.log('🏡 Owner fields:', {
      owner_type: propertyData.owner_type,
      owner_first_name: propertyData.owner_first_name,
      owner_last_name: propertyData.owner_last_name,
      owner_entity_name: propertyData.owner_entity_name,
      owner_entity_type: propertyData.owner_entity_type,
      owner_email: propertyData.owner_email,
      owner_phone: propertyData.owner_phone
    });
    
    try {
      console.log('📡 Creating property via backend API...');
      
      // Generate secure slug for new property
      const slug = generateSecurePropertySlug();
      const propertyDataWithSlug = {
        ...propertyData,
        slug: slug
      };
      
      console.log('🔗 Generated secure slug:', slug);
      const backendProperty = await showingbookerAPI.createProperty(propertyDataWithSlug);
      console.log('✅ Property created in backend:', backendProperty);
      
      // Refresh properties from backend
      await loadPropertiesFromBackend();
      alert('✅ Property added successfully!');
    } catch (error) {
      console.error('❌ Error adding property:', error);
      alert(`❌ Failed to add property: ${error.message}`);
    }
  };

  // Update property (backend only)
  const handlePropertyUpdate = async (propertyId, updates) => {
    if (!checkAuthentication()) {
      alert('❌ Please log in to update properties');
      return;
    }
    
    console.log('🔄 Updating property:', propertyId, updates);
    
    try {
      console.log('📡 Updating property via backend API...');
      await showingbookerAPI.updateProperty(propertyId, updates);
      console.log('✅ Property updated in backend');
      
      // Refresh properties from backend
      await loadPropertiesFromBackend();
      console.log('✅ Property updated');
    } catch (error) {
      console.error('❌ Error updating property:', error);
      alert(`❌ Failed to update property: ${error.message}`);
    }
  };

  // Delete property (backend only)
  const handleDelete = async (propertyId) => {
    if (!checkAuthentication()) {
      alert('❌ Please log in to delete properties');
      return;
    }
    
    const confirmDelete = window.confirm('Are you sure you want to delete this property? This action cannot be undone.');
    if (!confirmDelete) return;
    
    console.log('🗑️ Deleting property:', propertyId);
    
    try {
      console.log('📡 Deleting property via backend API...');
      await showingbookerAPI.deleteProperty(propertyId);
      console.log('✅ Property deleted from backend');
      
      // Refresh properties from backend
      await loadPropertiesFromBackend();
      alert('🗑️ Property deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting property:', error);
      alert(`❌ Failed to delete property: ${error.message}`);
    }
  };

  const handleViewBookings = (propertyId) => {
    if (!checkAuthentication()) {
      alert('❌ Please log in to view bookings');
      return;
    }
    const property = properties.find(p => p.id === propertyId);
    if (property) {
      setSelectedPropertyForBookings(property);
    }
  };

  const handleViewLogs = (propertyId) => {
    if (!checkAuthentication()) {
      alert('❌ Please log in to view compliance logs');
      return;
    }
    const property = properties.find(p => p.id === propertyId);
    if (property) {
      setSelectedPropertyForLogs(property);
    }
  };

  const closeLogs = () => {
    setSelectedPropertyForLogs(null);
  };

  const closeBookings = () => {
    setSelectedPropertyForBookings(null);
  };

  const handleCopyLink = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    const bookingUrl = property?.slug 
      ? `${window.location.origin}/book/${property.slug}`
      : `${window.location.origin}/book/${propertyId}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(bookingUrl);
      alert('🔗 Booking link copied to clipboard!');
    } else {
      prompt('Copy this booking link:', bookingUrl);
    }
  };


  // Show loading state
  if (loading) {
    return <LoadingMessage>Loading your properties...</LoadingMessage>;
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginPrompt>
        <h2>🔐 Please Login to Access ShowingBooker</h2>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          You need to be logged in to manage your property listings and bookings.
        </p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <a 
            href="/login" 
            style={{
              padding: '12px 24px',
              background: '#007AFF',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold'
            }}
          >
            Login
          </a>
          <a 
            href="/register" 
            style={{
              padding: '12px 24px',
              background: '#28a745',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold'
            }}
          >
            Register
          </a>
        </div>
      </LoginPrompt>
    );
  }

  // Show dashboard if authenticated
  return (
    <>
      <Dashboard>
        <TabContainer>
          <TabContent>
            {activeContent === 'overview' && (
              <Overview />
            )}


            {activeContent === 'calendar' && (
              <Calendar />
            )}

            {activeContent === 'property-listings' && (
              <>
                <Header>
                  <Title>
                    🏠 {userRole === 'admin' ? 'All Listings' : 'Your Listings'} ({properties.length})
                  </Title>
                  <AddButton onClick={() => setShowAddPropertyModal(true)}>
                    ➕ Add Property
                  </AddButton>
                </Header>

                {/* Property Search */}
                <PropertySearch 
                  properties={properties}
                  onFilteredProperties={setFilteredProperties}
                  isAdmin={userRole === 'admin'}
                />
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>
                    {filteredProperties.filter(p => p.status === propertyFilter).length} 
                    {propertyFilter === 'listed' ? ' Public' : ' Private'} Properties
                  </h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setPropertyFilter('listed')}
                      style={{
                        padding: '8px 16px',
                        background: propertyFilter === 'listed' ? '#000000' : '#f0f0f0',
                        color: propertyFilter === 'listed' ? 'white' : '#333',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: propertyFilter === 'listed' ? 'bold' : 'normal'
                      }}
                    >
                      Public
                    </button>
                    <button
                      onClick={() => setPropertyFilter('unlisted')}
                      style={{
                        padding: '8px 16px',
                        background: propertyFilter === 'unlisted' ? '#000000' : '#f0f0f0',
                        color: propertyFilter === 'unlisted' ? 'white' : '#333',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: propertyFilter === 'unlisted' ? 'bold' : 'normal'
                      }}
                    >
                      Private
                    </button>
                  </div>
                </div>
                <PropertiesGrid>
                  {filteredProperties.filter(p => p.status === propertyFilter).length > 0 ? (
                    filteredProperties.filter(p => p.status === propertyFilter).map(property => (
                      <PropertyCard 
                        key={property.id} 
                        property={property}
                        userProfile={userProfile}
                        onUpdate={handlePropertyUpdate}
                        onViewBookings={handleViewBookings}
                        onViewLogs={handleViewLogs}
                        onCopyLink={handleCopyLink}
                        onDelete={handleDelete}
                      />
                    ))
                  ) : (
                    <LoadingMessage>
                      <p>No {propertyFilter} properties. {propertyFilter === 'unlisted' ? 'Properties marked as unlisted will appear here.' : 'Add your first property using the Add Property tab!'}</p>
                    </LoadingMessage>
                  )}
                </PropertiesGrid>
              </>
            )}

            {activeContent === 'owners' && (
              <Owners />
            )}

            {activeContent === 'residents' && (
              <Residents />
            )}

            {activeContent === 'agents' && (
              <Agents />
            )}

            {activeContent === 'buyers' && (
              <Buyers />
            )}

            {activeContent === 'renters' && (
              <Renters />
            )}

            {activeContent === 'prospects' && (
              <Prospects />
            )}

            {activeContent === 'upcoming-showings' && (
              <UpcomingShowings properties={properties} />
            )}

            {activeContent === 'completed-showings' && (
              <CompletedShowings properties={properties} />
            )}

            {activeContent === 'profile' && (
              <ShowingProfile user={auth.currentUser} userProfile={userProfile} />
            )}

            {activeContent === 'stats' && (
              <Stats />
            )}

            {activeContent === 'support' && (
              <Support />
            )}
          </TabContent>
        </TabContainer>
      </Dashboard>

      {/* Property Logs Modal */}
      {selectedPropertyForLogs && (
        <PropertyLogs 
          property={selectedPropertyForLogs}
          onClose={closeLogs}
        />
      )}

      {/* Booking Management Modal */}
      {selectedPropertyForBookings && (
        <BookingManagement 
          property={selectedPropertyForBookings}
          onClose={closeBookings}
        />
      )}

      {/* Add Property Modal */}
      {showAddPropertyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #e9ecef',
              paddingBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '24px' }}>
                ➕ Add New Property
              </h3>
              <button
                onClick={() => setShowAddPropertyModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </div>
            
            <PropertyForm 
              onSubmit={(propertyData) => {
                addProperty(propertyData);
                setShowAddPropertyModal(false);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AgentDashboard;